import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Skeleton,
  Space,
  Button,
  message,
  Image,
  Alert,
} from 'antd';
import { ArrowLeftOutlined, BorderOutlined } from '@ant-design/icons';
import { InputNumber, Divider } from 'antd';
import { getPackage, listSamples, updatePackage, type ContentPackage, type PackageSample } from '@/api/content';
import { getPackagePlates, type Plate } from '@/api/plates';
import { bindLoRA, reviewPackage, triggerSampleAutogen } from '@/api/lora';
import { formatCNY, formatDateTime } from '@/utils/format';
import { FaceSwapConfigCard } from './FaceSwapConfigCard';
import { PlatePipelineConfigCard } from './PlatePipelineConfigCard';

export function ContentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState<ContentPackage | null>(null);
  const [samples, setSamples] = useState<PackageSample[]>([]);
  const [plates, setPlates] = useState<Plate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loraId, setLoraId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const loadSamples = (pkgId: number) => {
    listSamples(pkgId)
      .then((rows) => setSamples(rows ?? []))
      .catch(() => setSamples([]));
    getPackagePlates(pkgId)
      .then((rows) => setPlates(rows ?? []))
      .catch(() => setPlates([]));
  };

  const reload = () => {
    if (!id) return;
    getPackage(Number(id)).then(setPkg).catch(() => {});
    loadSamples(Number(id));
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getPackage(Number(id))
      .then(setPkg)
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
    loadSamples(Number(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onBind = async () => {
    if (!pkg || !loraId) return;
    setBusy(true);
    try {
      await bindLoRA(pkg.id, loraId);
      message.success(`已绑定 LoRA #${loraId}`);
      reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '绑定失败');
    } finally {
      setBusy(false);
    }
  };
  const onReview = async (to: string) => {
    if (!pkg) return;
    setBusy(true);
    try {
      await reviewPackage(pkg.id, to);
      message.success(`套系 → ${to}`);
      reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '流转失败');
    } finally {
      setBusy(false);
    }
  };
  const onSetCover = async (p: Plate) => {
    if (!pkg || !p.plate_image_key) {
      message.error('该底版缺少 OSS key, 无法设为封面');
      return;
    }
    setBusy(true);
    try {
      await updatePackage(pkg.id, { cover_url: p.plate_image_key });
      message.success('封面已更新');
      reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '设置失败');
    } finally {
      setBusy(false);
    }
  };
  const onSample = async () => {
    if (!pkg) return;
    setBusy(true);
    try {
      const r = await triggerSampleAutogen(pkg.id, 4);
      message.success(`样片生成已触发: ${r.run_id}`);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '触发失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/content')}>
          返回列表
        </Button>
        <Button icon={<BorderOutlined />} onClick={() => navigate(`/content/${id}/plates`)}>
          底版管理（候选挑选 / 标框 / 上架）
        </Button>
      </Space>
      {loading || !pkg ? (
        <Skeleton active />
      ) : (
        <Card
          title={pkg.name}
          extra={
            <Space>
              <Tag color="blue">app#{pkg.app_id}</Tag>
              <Tag>v{pkg.current_version ?? 1}</Tag>
              <Tag color="green">{pkg.status}</Tag>
            </Space>
          }
        >
          {pkg.cover_url && (
            <Image
              src={pkg.cover_url}
              alt={pkg.name}
              style={{ maxHeight: 200, marginBottom: 16, borderRadius: 6 }}
            />
          )}
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Code">{pkg.code}</Descriptions.Item>
            <Descriptions.Item label="工作流">{pkg.workflow_code || '-'}</Descriptions.Item>
            <Descriptions.Item label="售价">{formatCNY(pkg.price_cents)}</Descriptions.Item>
            <Descriptions.Item label="消耗钻石">{pkg.diamond_cost ?? 0}</Descriptions.Item>
            <Descriptions.Item label="prompt template">
              {pkg.prompt_template_id ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">{formatDateTime(pkg.created_at)}</Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">content-scale-lora</Divider>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="绑定 LoRA">{pkg.lora_model_id ?? '（未绑定）'}</Descriptions.Item>
          </Descriptions>
          <Space wrap style={{ marginTop: 8 }}>
            <InputNumber
              placeholder="lora_model_id"
              min={1}
              value={loraId ?? undefined}
              onChange={(v) => setLoraId(typeof v === 'number' ? v : null)}
            />
            <Button onClick={onBind} loading={busy} disabled={!loraId}>
              绑定 LoRA
            </Button>
            <Button onClick={onSample} loading={busy}>
              样片自动生成 ×4
            </Button>
          </Space>
          <FaceSwapConfigCard pkg={pkg} onSaved={reload} />
          <PlatePipelineConfigCard pkg={pkg} onSaved={reload} />

          {/* 底版 = package_plates(换脸底图, 底版管理页维护); 样片 = package_samples
              (样片自动生成产物)。此前本区读 samples 却标「底版照片」, 有底版时仍显示
              0 张 (2026-08-09 中秋套系困惑根因) — 拆成两区各读各表。 */}
          <Divider orientation="left">
            底版照片（{plates.length} 张 · 已上架 {plates.filter((p) => p.status === 'active').length}）
          </Divider>
          {plates.length === 0 ? (
            <Alert type="info" showIcon message="该套系暂无底版 — 到「底版管理」生成/选入" />
          ) : (
            <Image.PreviewGroup>
              <Space wrap size={[8, 8]}>
                {plates.map((p) => (
                  <div key={p.plate_id} style={{ textAlign: 'center' }}>
                    <Image
                      src={p.plate_image_url}
                      width={110}
                      height={150}
                      style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                    />
                    <div style={{ fontSize: 11 }}>
                      <Tag color={p.status === 'active' ? 'green' : p.status === 'draft' ? 'blue' : 'default'} style={{ marginRight: 0 }}>
                        {p.status === 'active' ? '已上架' : p.status === 'draft' ? '待标框/上架' : p.status}
                      </Tag>{' '}
                      <span style={{ color: '#999' }}>{p.faces?.length ?? 0} 框</span>
                    </div>
                    <Button size="small" type="link" style={{ fontSize: 11, padding: 0 }} onClick={() => onSetCover(p)}>
                      设为封面
                    </Button>
                  </div>
                ))}
              </Space>
            </Image.PreviewGroup>
          )}

          <Divider orientation="left">样片（自动生成产物，{samples.length} 张）</Divider>
          {samples.length === 0 ? (
            <Alert type="info" showIcon message="暂无样片 — 可点上方「样片自动生成 ×4」" />
          ) : (
            <Image.PreviewGroup>
              <Space wrap size={[8, 8]}>
                {samples.map((s, i) => (
                  <div key={s.id} style={{ textAlign: 'center' }}>
                    <Image
                      src={s.url}
                      width={110}
                      height={150}
                      style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                    />
                    <div style={{ fontSize: 11, color: '#999' }}>#{i + 1}</div>
                  </div>
                ))}
              </Space>
            </Image.PreviewGroup>
          )}

          <Divider orientation="left">审核状态机（active 必经 reviewing）</Divider>
          <Space wrap>
            <Tag color="green">{pkg.status}</Tag>
            <Button size="small" onClick={() => onReview('reviewing')} loading={busy}>
              → reviewing
            </Button>
            <Button size="small" type="primary" onClick={() => onReview('active')} loading={busy}>
              approve → active
            </Button>
            <Button size="small" danger onClick={() => onReview('archived')} loading={busy}>
              → archived
            </Button>
          </Space>
        </Card>
      )}
    </div>
  );
}
